import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedClassDetail,
  summarizeYowanClassSchedules,
} from '../app/yowanContentClient';
import {
  getStakeholderClassApplicationsHref,
  StakeholderGuardNotice,
  StakeholderShell,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const ReadOnlyField = ({ label, value, multiline = false, rows = 3 }) => {
  const safeValue = value || '-';

  return (
    <label className={`c-form__field yowan-admin-readonly-field${multiline ? ' yowan-admin-readonly-field--wide' : ''}`}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea className="c-form__control c-form__control--textarea" readOnly rows={rows} value={safeValue} />
      ) : (
        <input className="c-form__control" readOnly type="text" value={safeValue} />
      )}
    </label>
  );
};

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
    <header className="yowan-admin-event-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const formatClassScheduleSummaryLine = (item = {}) =>
  [item.datetime, item.location, item.instructor].filter(Boolean).join(' / ') || '-';

const formatClassScheduleSummaryText = (record = {}, { limit = 3 } = {}) => {
  const summary = summarizeYowanClassSchedules(record, { limit });
  const items = Array.isArray(summary?.items) ? summary.items : [];

  if (items.length === 0) {
    return '';
  }

  const lines = items.map(formatClassScheduleSummaryLine);

  if (summary.remainingCount) {
    lines.push(`ほか${summary.remainingCount}件`);
  }

  return lines.join('\n');
};

const StakeholderClassDetailPage = ({ guardOptions }) => {
  const { classId } = useParams();
  const content = yowanPageContent['/stakeholder/classes/:classId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanRelatedClassDetail(classId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError(content?.loadErrorMessage || '担当教室・講座詳細を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [classId, content?.loadErrorMessage, guard.status]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell activeNavHref="/stakeholder/classes" routeKey="/stakeholder/classes/:classId">
      <AdminPageHeader
        title={record ? `${record.title} 詳細` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="applications" to={getStakeholderClassApplicationsHref(classId, preservedDomainSearch)}>
            申込一覧
          </Link>,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="担当教室・講座詳細を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <DetailSection title="基本情報">
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '有効' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
                <StatusBadge label={record.acceptsApplications ? '受付可' : '受付停止'} tone={record.acceptsApplications ? 'published' : 'draft'} />
              </div>
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="教室・講座名" value={record.title} />
                <ReadOnlyField label="主催者" value={record.organizerName} />
                <ReadOnlyField label="講師" value={record.standardInstructor} />
                <ReadOnlyField label="開催予定" value={formatClassScheduleSummaryText(record, { limit: 3 })} multiline rows={4} />
                <ReadOnlyField label="申込者数" value={`${record.applicationsCount || 0}名`} />
                <ReadOnlyField label="概要" value={record.summary} multiline rows={3} />
                <ReadOnlyField label="案内文" value={record.guidance} multiline rows={5} />
              </div>
            </DetailSection>

            <DetailSection title="開催スケジュール">
              {record.schedules?.length ? (
                <div className="yowan-stakeholder-slot-list">
                  {record.schedules.map((schedule) => (
                    <article key={schedule.id || schedule.datetimeLabel} className="yowan-stakeholder-slot">
                      <h3>{schedule.scheduleLabel || schedule.weekdayLabel || schedule.datetimeLabel || '開催スケジュール'}</h3>
                      <dl>
                        <div><dt>日時</dt><dd>{schedule.datetimeLabel || '-'}</dd></div>
                        <div><dt>会場</dt><dd>{schedule.locationLabel || schedule.roomName || '-'}</dd></div>
                        <div><dt>講師</dt><dd>{schedule.instructorName || '-'}</dd></div>
                        <div><dt>参加費</dt><dd>{schedule.feeLabel || '-'}</dd></div>
                        <div><dt>定員</dt><dd>{schedule.capacity ? `${schedule.capacity}名` : '-'}</dd></div>
                      </dl>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="yowan-admin-form__message">開催スケジュールは登録されていません。</p>
              )}
            </DetailSection>
          </div>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderClassDetailPage;
